import { useEffect, useMemo } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { Queue, QueueSettingsUploadValues } from '@rossum/api-client/queues';
import {
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { QueueList } from '../../queues/select-queue/QueueList';
import { WorkspaceWithQueues } from '../../queues/select-queue/types';
import { AdditionalUploadValues, determineSelectInputError } from './helpers';
import { ListSelect } from './ListSelect';

const FORM_WIDTH = 350;

type Props = {
  onConfirm: (params: AdditionalUploadValues) => void;
  selectedQueue: Queue | undefined;
  setSelectedQueue: (_queue: Queue | undefined) => void;
  workspacesWithQueues?: WorkspaceWithQueues[];
  onFormStateChange?: (hasErrors: boolean) => void;
};

const uploadValuesSchema = z
  .array(
    z.object({
      id: z.string(),
      label: z.string(),
      required: z.boolean().default(false),
      type: z
        .union([z.literal('values'), z.literal('metadata')])
        .default('values')
        .catch('values'),
      data_type: z
        .union([z.literal('string'), z.literal('enum')])
        .default('string'),
      enum_options: z
        .array(z.object({ label: z.string(), value: z.string() }))
        .default([])
        .catch([]),
    })
  )
  .default([])
  .catch([]);

const createValidationSchema = (
  fields: QueueSettingsUploadValues[] = [],
  intl: IntlShape
) => {
  const schemaFields = fields.reduce<Record<string, z.ZodTypeAny>>(
    (acc, field) => {
      const requiredMsg = intl.formatMessage(
        {
          id: 'containers.annotationList.upload.additionalValues.fieldRequired',
        },
        {
          fieldLabel: field.label,
        }
      );

      if (field.data_type === 'enum') {
        const allowed: string[] = field.enum_options?.map(o => o.value) ?? [];
        const requiredEnum = z
          .string()
          .trim()
          .min(1, requiredMsg)
          .refine(v => allowed.includes(v), { message: requiredMsg });
        const optionalEnum = z
          .union([
            z.literal(''),
            z
              .string()
              .refine(v => allowed.includes(v), { message: requiredMsg }),
          ])
          .optional();

        const enumValidator = field.required ? requiredEnum : optionalEnum;
        return { ...acc, [field.id]: enumValidator };
      }

      const validator = field.required
        ? z.string().trim().min(1, requiredMsg)
        : z.string().optional();
      return { ...acc, [field.id]: validator };
    },
    {}
  );

  return z.object(schemaFields);
};

export const ValuesFooter = ({
  onConfirm,
  workspacesWithQueues,
  selectedQueue,
  setSelectedQueue,
  onFormStateChange,
}: Props) => {
  const intl = useIntl();

  const queueAdditionalValues = selectedQueue?.settings.uploadValues;

  const parsedUploadValues = uploadValuesSchema.parse(queueAdditionalValues);

  const schema = useMemo(
    () => createValidationSchema(parsedUploadValues, intl),
    [parsedUploadValues, intl]
  );

  const defaultValues = useMemo(
    () =>
      parsedUploadValues.reduce<Record<string, string>>((acc, field) => {
        acc[field.id] = '';
        return acc;
      }, {}),
    [parsedUploadValues]
  );

  // metadata vs values identification is necessary: https://rossum.app/api/docs/openapi/api/upload/#create-upload
  // Since type information has no business in the form as it is not exposed to the user
  // we keep `type` out of the form and utilise parsedUploadValues to identify the type after submit
  const normalizeFieldValues = (fieldValues: z.TypeOf<typeof schema>) => {
    const validFieldEntries = R.entries(fieldValues).flatMap(([key, value]) =>
      value ? [[key, value] as const] : []
    );

    const [valuesEntries, metadataEntries] = R.partition(
      validFieldEntries,
      ([key]) =>
        parsedUploadValues.some(
          uploadValue => uploadValue.type === 'values' && uploadValue.id === key
        )
    );
    return {
      values: R.fromEntries(valuesEntries),
      metadata: R.fromEntries(metadataEntries),
    };
  };

  const {
    control,
    handleSubmit,
    formState: { submitCount, errors, isValid },
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues,
  });

  const inputError = useMemo(
    () => determineSelectInputError(submitCount, Boolean(selectedQueue), intl),
    [intl, selectedQueue, submitCount]
  );

  useEffect(() => {
    onFormStateChange?.(Object.keys(errors).length > 0 || !isValid);
  }, [errors, isValid, onFormStateChange]);

  return (
    <Stack mt="auto" width="100%">
      <Stack
        spacing={3}
        component="form"
        onSubmit={handleSubmit(fieldValues =>
          onConfirm(normalizeFieldValues(fieldValues))
        )}
        id="upload-additional-values-form"
      >
        {workspacesWithQueues ? (
          <ListSelect
            isLoading={workspacesWithQueues.length === 0}
            value={selectedQueue}
            selectPlaceholder={intl.formatMessage({
              id: 'containers.annotationList.upload.queueSelect.placeholder',
            })}
            label={intl.formatMessage({
              id: 'containers.annotationList.upload.queueSelect.label',
            })}
            onCloseClick={e => {
              e.stopPropagation();
              setSelectedQueue(undefined);
            }}
            selectWidth={FORM_WIDTH}
            error={inputError}
          >
            {({ close }) => (
              <QueueList
                onChange={selected => {
                  if (selected === selectedQueue) {
                    // deselect the option but do not close the menu
                    return setSelectedQueue(undefined);
                  }
                  setSelectedQueue(selected);
                  return close();
                }}
                value={selectedQueue}
                workspaces={workspacesWithQueues}
              />
            )}
          </ListSelect>
        ) : null}
        {parsedUploadValues?.length ? (
          <Stack spacing={2} direction="column">
            <Divider />
            <Typography color="text.secondary">
              {intl.formatMessage({
                id: 'containers.annotationList.upload.additionalValues',
              })}
            </Typography>
            <Stack
              gap={1}
              direction="row"
              flexWrap="wrap"
              sx={{ maxHeight: '20vh', overflow: 'auto', pt: 1 }}
            >
              {parsedUploadValues.map(field =>
                field.data_type === 'enum' ? (
                  <Controller
                    key={field.id}
                    control={control}
                    name={field.id}
                    defaultValue=""
                    render={({ field: rhfField, fieldState }) => (
                      <TextField
                        select
                        size="small"
                        sx={{ width: FORM_WIDTH }}
                        label={field.label}
                        required={field.required}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        {...rhfField}
                      >
                        {field.enum_options.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                ) : (
                  <TextFieldControl
                    sx={{ width: FORM_WIDTH }}
                    key={field.id}
                    FieldLabelProps={{ layout: 'floating' }}
                    ControllerProps={{ control, name: field.id }}
                    label={field.label}
                    size="small"
                  />
                )
              )}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};
